/**
 * Declarative config-field vocabulary for the widget engine.
 *
 * Each widget's config UI is described by an array of these field definitions
 * (see `WidgetSchema.fields` in ./types). A field maps to (a) one control in the
 * shared config panel and (b) one or more keys in the generated embed config.
 *
 * Keys are camelCase and double as the nuqs URL param + the config-state key.
 */

export type FieldKind =
    | 'event-search'
    | 'market-select'
    | 'theme'
    | 'dimensions'
    | 'category-select'
    | 'utm'
    | 'enum'
    | 'boolean'
    | 'color'
    | 'text';

export interface DimensionsValue {
    width: string;
    height: string;
}

export interface UtmValue {
    utmSource: string;
    utmCampaign: string;
    promoCode: string;
}

export type ThemeValue = 'light' | 'dark';

interface BaseField<K extends FieldKind, V> {
    kind: K;
    /** Config-state key AND nuqs URL param (camelCase). */
    key: string;
    label: string;
    description?: string;
    defaultValue: V;
    /** Contributes to the embed config but is not rendered as a control. */
    hidden?: boolean;
    /** Only render the control when this predicate passes (e.g. multi-market events). */
    visibleWhen?: (config: WidgetConfigState) => boolean;
}

export interface EventSearchField extends BaseField<'event-search', string> {
    /** Allow pasting a raw ticker as a fallback (default true). */
    allowManualTicker?: boolean;
}

export interface MarketSelectField extends BaseField<'market-select', string[]> {
    mode: 'single' | 'multi' | 'orderable';
    min?: number;
    max?: number;
    /** Auto-select the top-N markets by last price when the event loads. */
    autoSelectTopN?: number;
}

export type ThemeField = BaseField<'theme', ThemeValue>;

export interface DimensionsField extends BaseField<'dimensions', DimensionsValue> {
    /** Lock dimensions (e.g. KPOW = fixed 560px height). */
    fixed?: Partial<DimensionsValue>;
}

export interface CategorySelectField extends BaseField<'category-select', string> {
    options: { value: string; label: string }[];
}

export type UtmField = BaseField<'utm', UtmValue>;

export interface EnumField extends BaseField<'enum', string> {
    options: { value: string; label: string }[];
    control?: 'select' | 'segmented';
}

export type BooleanField = BaseField<'boolean', boolean>;

export type ColorField = BaseField<'color', string>;

/** Free-text input forwarded as a direct-iframe query param when non-empty (e.g. `partner`). */
export interface TextField extends BaseField<'text', string> {
    placeholder?: string;
    maxLength?: number;
    /**
     * Regex character-class body (e.g. `A-Za-z0-9._-`) of the only characters
     * the embed accepts; anything else is stripped as it's typed. A string, not
     * a RegExp, because the schema crosses the Server→Client boundary.
     */
    allowedCharacters?: string;
}

/** `value` with any characters outside the field's `allowedCharacters` removed. */
export function sanitizeTextFieldValue(field: TextField, value: string): string {
    return field.allowedCharacters ? value.replace(new RegExp(`[^${field.allowedCharacters}]`, 'g'), '') : value;
}

export type AnyField =
    | EventSearchField
    | MarketSelectField
    | ThemeField
    | DimensionsField
    | CategorySelectField
    | UtmField
    | EnumField
    | BooleanField
    | ColorField
    | TextField;

/** Flat config state keyed by `field.key`. */
export type WidgetConfigState = Record<string, unknown>;
